import type { CatalogFrontendService } from "../../../cbi-generator/frontend/services/CatalogFrontendService";
import type { CatalogItemInstanceStaff } from "../../../cbi-generator/generated/dto/CatalogItemInstanceStaff";
import type { CatalogItemStaff } from "../../../cbi-generator/generated/dto/CatalogItemStaff";
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import { BalanceType } from "../../../cbi-generator/generated/dto/BalanceType";

export async function loadStaffCatalog(
  service: CatalogFrontendService,
  restockBalanceType?: BalanceType,
) {
  const [
    instances,
    catalogs,
    categories,
    categoryItems,
    agreements,
    agreementCategories,
  ] = await Promise.all([
    service.getAllCatalogItemInstanceStaffs({
      itemId: true,
      stockChanges: false,
    }),
    service.getAllCatalogStaffs({
      products: false,
      items: false,
      discounts: false,
      taxes: false,
      agreements: false,
    }),
    service.getAllCategoryStaffs(),
    service.getAllCatalogItemStaffs({
      catalogId: false,
      productId: false,
      itemInstances: false,
      categories: true,
      agreements: true,
      itemDiscounts: false,
      taxes: false,
    }),
    service.getAllAgreementStaffs({
      catalogId: false,
      items: false,
      agreementInstances: true,
      agreementCategories: false,
    }),
    service.getAllAgreementCategoryStaffs({
      agreementId: false,
      categoryId: false,
    }),
  ]);
  const now = Date.now();
  const activeCatalogs = catalogs.filter(
    (catalog) =>
      catalog.startActive != null &&
      catalog.startActive.getTime() <= now &&
      (catalog.endActive == null || catalog.endActive.getTime() > now),
  );
  if (activeCatalogs.length !== 1)
    throw new Error(
      activeCatalogs.length === 0
        ? "No catalog is currently active."
        : "More than one catalog is currently active. Correct the catalog sale dates before shopping.",
    );
  const activeCatalogId = activeCatalogs[0].assignId;
  const activeAgreementRecords = agreements.filter((agreement) => {
    const isInActiveCatalog =
      agreement.catalogIdTargetIds.assignId === activeCatalogId;
    const hasActiveInstance = agreement.agreementInstances?.some(
      (instance) =>
        instance.startActive != null &&
        instance.startActive.getTime() <= now &&
        (instance.endActive == null || instance.endActive.getTime() > now),
    );
    return isInActiveCatalog && hasActiveInstance;
  });
  const activeAgreementIds = new Set(
    activeAgreementRecords.map((agreement) => agreement.assignId),
  );
  const globalAgreementIds = activeAgreementRecords
    .filter((agreement) => agreement.isGlobal === true)
    .map((agreement) => agreement.assignId);
  const agreementNames = new Map(
    activeAgreementRecords.map((agreement) => [
      agreement.assignId,
      agreement.agreementName ?? `Agreement #${agreement.assignId}`,
    ]),
  );
  const categoryAgreementIds = new Map<number, Set<number>>();
  for (const link of agreementCategories) {
    const agreementId = link.agreementIdTargetIds?.assignId;
    if (agreementId == null || !activeAgreementIds.has(agreementId)) continue;
    const categoryId = link.categoryIdTargetIds.assignId;
    const linkedAgreements = categoryAgreementIds.get(categoryId) ?? new Set();
    linkedAgreements.add(agreementId);
    categoryAgreementIds.set(categoryId, linkedAgreements);
  }
  const names = new Map(
    catalogs.map((catalog) => [catalog.assignId, catalog.catalogName]),
  );
  const itemCatalogNames = new Map<number, string>();
  const items = instances
    .map((instance) => {
      if (!hasCatalogItem(instance))
        throw new Error(
          `Catalog instance #${instance.assignId} did not load its item. Refresh the shop.`,
        );
      const catalogId = instance.itemId.catalogIdTargetIds?.assignId;
      if (catalogId != null && !names.has(catalogId))
        throw new Error(
          `Catalog item #${instance.itemId.assignId} refers to a missing catalog. Refresh the shop.`,
        );
      itemCatalogNames.set(
        instance.itemId.assignId,
        (catalogId == null ? null : names.get(catalogId)) ?? "Uncategorized",
      );
      return instance;
    })
    .filter(
      (instance) =>
        instance.itemId.catalogIdTargetIds?.assignId === activeCatalogId &&
        (restockBalanceType != null
          ? instance.itemId.itemType === ItemType.GIFT_CARD &&
            (instance.itemId.balanceType ?? BalanceType.GIFT_CARD) ===
              restockBalanceType
          : instance.itemId.itemType !== ItemType.GIFT_CARD ||
            instance.itemId.balanceType == null ||
            instance.itemId.balanceType === BalanceType.GIFT_CARD) &&
        instance.startActive != null &&
        instance.startActive.getTime() <= now &&
        (instance.endActive == null || instance.endActive.getTime() > now),
    )
    .sort((a, b) => a.assignId - b.assignId);
  if (restockBalanceType != null && items.length !== 1)
    throw new Error(
      items.length === 0
        ? `No active catalog item can restock a ${restockBalanceType.replaceAll("_", " ").toLowerCase()} balance.`
        : `Multiple active catalog items can restock a ${restockBalanceType.replaceAll("_", " ").toLowerCase()} balance. Configure exactly one before restocking.`,
    );
  const itemCategoryIds = new Map(
    categoryItems.map((item) => {
      if (item.categories == null)
        throw new Error(
          `Catalog item #${item.assignId} did not load its categories.`,
        );
      return [
        item.assignId,
        item.categories.map(
          (category) => category.categoryIdTargetIds.assignId,
        ),
      ] as const;
    }),
  );
  const itemAgreementIds = new Map(
    categoryItems.map((item) => {
      if (item.agreements == null)
        throw new Error(
          `Catalog item #${item.assignId} did not load its agreements.`,
        );
      return [
        item.assignId,
        item.agreements
          .map((agreement) => agreement.agreementIdTargetIds.assignId)
          .filter((agreementId) => activeAgreementIds.has(agreementId)),
      ] as const;
    }),
  );
  const paidClassCategoryIds = new Set(
    items
      .filter((item) => item.itemId.itemType === ItemType.PAID_CLASS)
      .flatMap((item) => itemCategoryIds.get(item.itemId.assignId) ?? []),
  );
  const nonPaidCategoryIds = new Set(
    items
      .filter((item) => item.itemId.itemType !== ItemType.PAID_CLASS)
      .flatMap((item) => itemCategoryIds.get(item.itemId.assignId) ?? []),
  );
  return {
    items,
    itemCatalogNames,
    itemCategoryIds,
    itemAgreementIds,
    categoryAgreementIds: new Map(
      [...categoryAgreementIds].map(([id, linked]) => [id, [...linked]]),
    ),
    globalAgreementIds,
    agreementNames,
    categories: categories.filter(
      (category) =>
        category.catalogId === activeCatalogId &&
        category.categoryType === "ITEM_TAB" &&
        category.hidden !== true &&
        (!paidClassCategoryIds.has(category.assignId) ||
          nonPaidCategoryIds.has(category.assignId)),
    ),
  };
}

function hasCatalogItem(
  instance: CatalogItemInstanceStaff,
): instance is CatalogItemInstanceStaff & {
  readonly itemId: CatalogItemStaff;
} {
  return instance.itemId != null;
}
